import { useCallback } from 'react';
import { useQueryClient, QueryClient } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { useLocationGroupStore } from '@frontend/scope';
import { getStoredQueryClient } from './api';
import type { WetagQueryResponse, WetagQueryOptions, WetagResult, WetagInput } from './types';
import {
  useWetag,
  useWetagQuery,
  prefetchWetag,
  fetchWetagValue,
  getWetagFromCache,
  invalidateWetag,
} from './use-wetag';

function currentUserId(): string | undefined {
  const user = getUser();
  return user?.userID;
}

function useCurrentOrgId(): string {
  const orgId = useLocationGroupStore((state) => state.selectedOrgId);
  return orgId;
}

function getCurrentOrgId(): string | undefined {
  return useLocationGroupStore.getState().selectedOrgId;
}

type WetagHook<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions
) => [boolean, boolean, WetagQueryResponse | undefined];

type WetagQueryHook<TTag extends string> = (input: WetagInput<TTag>, options?: WetagQueryOptions) => WetagResult;

type UsePrefetchHook<TTag extends string> = () => (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions
) => Promise<void>;

type UseInvalidateHook<TTag extends string> = () => (input: WetagInput<TTag>) => Promise<void>;

type UseGetFromCacheHook<TTag extends string> = () => (input: WetagInput<TTag>) => boolean | undefined;

type PrefetchFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<void>;

type InvalidateFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: { queryClient?: QueryClient }
) => Promise<void>;

type GetFromCacheFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: { queryClient?: QueryClient }
) => boolean | undefined;

type FetchValueFnWithEntityId<TTag extends string> = (
  input: WetagInput<TTag>,
  entityId: string,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<boolean>;

type PrefetchFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<void>;

type InvalidateFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: { queryClient?: QueryClient }
) => Promise<void>;

type GetFromCacheFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: { queryClient?: QueryClient }
) => boolean | undefined;

type FetchValueFnWithGetter<TTag extends string> = (
  input: WetagInput<TTag>,
  options?: WetagQueryOptions & { queryClient?: QueryClient }
) => Promise<boolean>;

interface NamespacedHooksBase<TTag extends string> {
  /** Returns a tuple that looks like `[isEnabled, isLoading, data]`. Wetags are async so ensure you've handled loading states appropriately. */
  useWetag: WetagHook<TTag>;
  useWetagQuery: WetagQueryHook<TTag>;
  usePrefetch: UsePrefetchHook<TTag>;
  useInvalidate: UseInvalidateHook<TTag>;
  useGetFromCache: UseGetFromCacheHook<TTag>;
}

export interface NamespacedHooks<TTag extends string = string> extends NamespacedHooksBase<TTag> {
  prefetch: PrefetchFnWithEntityId<TTag>;
  invalidate: InvalidateFnWithEntityId<TTag>;
  getFromCache: GetFromCacheFnWithEntityId<TTag>;
  fetchValue: FetchValueFnWithEntityId<TTag>;
}

export interface NamespacedHooksWithGetter<TTag extends string = string> extends NamespacedHooksBase<TTag> {
  prefetch: PrefetchFnWithGetter<TTag>;
  invalidate: InvalidateFnWithGetter<TTag>;
  getFromCache: GetFromCacheFnWithGetter<TTag>;
  fetchValue: FetchValueFnWithGetter<TTag>;
}

function resolveQueryClient(providedClient?: QueryClient): QueryClient {
  if (providedClient) return providedClient;
  return getStoredQueryClient();
}

function createNamespacedWetagHooksWithGetter<TTag extends string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId: () => string | undefined
): NamespacedHooksWithGetter<TTag> {
  const useNamespacedWetag: WetagHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetag(namespace, input, entityId, options);
  };

  const useNamespacedWetagQuery: WetagQueryHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetagQuery(namespace, input, entityId, options);
  };

  const usePrefetch: UsePrefetchHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>, options?: WetagQueryOptions) => {
        if (!entityId) return Promise.resolve();
        return prefetchWetag(queryClient, namespace, input, entityId, options);
      },
      [queryClient, entityId]
    );
  };

  const useInvalidate: UseInvalidateHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return Promise.resolve();
        return invalidateWetag(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const useGetFromCache: UseGetFromCacheHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return undefined;
        return getWetagFromCache(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const prefetchFn: PrefetchFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve();
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return prefetchWetag(queryClient, namespace, input, entityId, wetagOptions);
  };

  const invalidateFn: InvalidateFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve();
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return invalidateWetag(queryClient, namespace, input, entityId);
  };

  const getFromCacheFn: GetFromCacheFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return undefined;
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return getWetagFromCache(queryClient, namespace, input, entityId);
  };

  const fetchValueFn: FetchValueFnWithGetter<TTag> = (input, options = {}) => {
    const entityId = getEntityId();
    if (!entityId) return Promise.resolve(false);
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return fetchWetagValue(queryClient, namespace, input, entityId, wetagOptions);
  };

  return {
    useWetag: useNamespacedWetag,
    useWetagQuery: useNamespacedWetagQuery,
    usePrefetch,
    useInvalidate,
    useGetFromCache,
    prefetch: prefetchFn,
    invalidate: invalidateFn,
    getFromCache: getFromCacheFn,
    fetchValue: fetchValueFn,
  };
}

function createNamespacedWetagHooksWithoutGetter<TTag extends string>(
  namespace: string,
  useGetEntityId: () => string | undefined
): NamespacedHooks<TTag> {
  const useNamespacedWetag: WetagHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetag(namespace, input, entityId, options);
  };

  const useNamespacedWetagQuery: WetagQueryHook<TTag> = (input, options = {}) => {
    const entityId = useGetEntityId();
    return useWetagQuery(namespace, input, entityId, options);
  };

  const usePrefetch: UsePrefetchHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>, options?: WetagQueryOptions) => {
        if (!entityId) return Promise.resolve();
        return prefetchWetag(queryClient, namespace, input, entityId, options);
      },
      [queryClient, entityId]
    );
  };

  const useInvalidate: UseInvalidateHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return Promise.resolve();
        return invalidateWetag(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const useGetFromCache: UseGetFromCacheHook<TTag> = () => {
    const queryClient = useQueryClient();
    const entityId = useGetEntityId();

    return useCallback(
      (input: WetagInput<TTag>) => {
        if (!entityId) return undefined;
        return getWetagFromCache(queryClient, namespace, input, entityId);
      },
      [queryClient, entityId]
    );
  };

  const prefetchFn: PrefetchFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return prefetchWetag(queryClient, namespace, input, entityId, wetagOptions);
  };

  const invalidateFn: InvalidateFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return invalidateWetag(queryClient, namespace, input, entityId);
  };

  const getFromCacheFn: GetFromCacheFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient } = options;
    const queryClient = resolveQueryClient(providedClient);
    return getWetagFromCache(queryClient, namespace, input, entityId);
  };

  const fetchValueFn: FetchValueFnWithEntityId<TTag> = (input, entityId, options = {}) => {
    const { queryClient: providedClient, ...wetagOptions } = options;
    const queryClient = resolveQueryClient(providedClient);
    return fetchWetagValue(queryClient, namespace, input, entityId, wetagOptions);
  };

  return {
    useWetag: useNamespacedWetag,
    useWetagQuery: useNamespacedWetagQuery,
    usePrefetch,
    useInvalidate,
    useGetFromCache,
    prefetch: prefetchFn,
    invalidate: invalidateFn,
    getFromCache: getFromCacheFn,
    fetchValue: fetchValueFn,
  };
}

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId: () => string | undefined
): NamespacedHooksWithGetter<TTag>;

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined
): NamespacedHooks<TTag>;

export function createNamespacedWetagHooks<TTag extends string = string>(
  namespace: string,
  useGetEntityId: () => string | undefined,
  getEntityId?: () => string | undefined
): NamespacedHooks<TTag> | NamespacedHooksWithGetter<TTag> {
  if (getEntityId) {
    return createNamespacedWetagHooksWithGetter<TTag>(namespace, useGetEntityId, getEntityId);
  }
  return createNamespacedWetagHooksWithoutGetter<TTag>(namespace, useGetEntityId);
}

export function createNamespacedWetagHooksForUser<TTag extends string = string>(
  namespace: string
): NamespacedHooksWithGetter<TTag> {
  return createNamespacedWetagHooksWithGetter<TTag>(namespace, currentUserId, currentUserId);
}

export function createNamespacedWetagHooksForOrg<TTag extends string = string>(
  namespace: string
): NamespacedHooksWithGetter<TTag> {
  return createNamespacedWetagHooksWithGetter<TTag>(namespace, useCurrentOrgId, getCurrentOrgId);
}
